import { useEffect, useMemo, useRef, useState } from 'react';
import type { PowerPointContent } from '@/lib/documents';
import type { DeckChart, DeckElement, DeckShape, DeckSlide, SlideDeck } from '@/lib/slideDeck';
import {
  DEFAULT_THEME,
  LAYOUTS,
  THEME_PRESETS,
  buildLayout,
  clamp,
  deckFromContent,
  deckToLegacySlides,
  escapeHtml,
  isDarkSlide,
  textElement,
  uid,
} from '@/lib/slideDeck';
import DeckSlideStatic from '@/components/feature/presentation/DeckSlideStatic';
import DocsShareModal from '@/components/feature/DocsShareModal';
import WordMenuBar from './WordMenuBar';
import { EditorShortcutsDialog, EditorAboutDialog } from './EditorHelpDialogs';
import { WordPromptDialog } from './WordDialogs';
import SlideCanvas from './presentation/SlideCanvas';
import SlideInspector from './presentation/SlideInspector';
import SlideInsertDialogs, { type InsertKind } from './presentation/SlideInsertDialogs';
import PresentationAiPanel from './presentation/PresentationAiPanel';
import { buildPowerPointMenus, type PowerPointMenuHandlers } from './powerpointMenus';

interface PowerPointEditorProps {
  content: PowerPointContent;
  onChange: (content: PowerPointContent) => void;
  readOnly?: boolean;
  docName?: string;
  canManage?: boolean;
  documentId?: number;
  currentUser?: { id: number; name: string };
  onSave?: () => void;
  onRename?: (name: string) => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onClose?: () => void;
  onShare?: () => void;
  onHistory?: () => void;
  onExport?: (format: 'pptx' | 'pdf') => void;
}

const ZOOMS = [75, 100, 125, 150];

export default function PowerPointEditor({
  content,
  onChange,
  readOnly = false,
  docName = 'Présentation',
  canManage = false,
  documentId,
  currentUser,
  onSave,
  onRename,
  onDuplicate,
  onDelete,
  onClose,
  onShare,
  onHistory,
  onExport,
}: PowerPointEditorProps) {
  const [deck, setDeck] = useState<SlideDeck>(() => deckFromContent(content as unknown as Record<string, unknown>));
  const [currentId, setCurrentId] = useState<string>(() => deck.slides[0]?.id || '');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(100);
  const [showInspector, setShowInspector] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [insertKind, setInsertKind] = useState<InsertKind | null>(null);
  const [present, setPresent] = useState(false);
  const [presentIndex, setPresentIndex] = useState(0);
  const [showNotes, setShowNotes] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [showPublicShare, setShowPublicShare] = useState(false);

  const emittedRef = useRef<PowerPointContent>(content);
  const deckRef = useRef<SlideDeck>(deck);
  const toastTimer = useRef<number | null>(null);

  const notify = (message: string) => {
    setToast(message);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2600);
  };

  const commit = (next: SlideDeck) => {
    deckRef.current = next;
    setDeck(next);
    const payload = { ...(content as Record<string, unknown>), deck: next, slides: deckToLegacySlides(next) } as unknown as PowerPointContent;
    emittedRef.current = payload;
    onChange(payload);
  };

  // Synchronisation si le contenu est remplacé de l'extérieur (historique, IA…)
  useEffect(() => {
    if (content === emittedRef.current) return;
    const next = deckFromContent(content as unknown as Record<string, unknown>);
    deckRef.current = next;
    setDeck(next);
    setCurrentId((prev) => (next.slides.some((s) => s.id === prev) ? prev : next.slides[0]?.id || ''));
    emittedRef.current = content;
  }, [content]);

  const slides = deck.slides;
  const currentIndex = Math.max(0, slides.findIndex((s) => s.id === currentId));
  const current: DeckSlide = slides[currentIndex] || slides[0];
  const selected = current?.elements.find((e) => e.id === selectedId) || null;
  const theme = deck.theme || DEFAULT_THEME;
  const textColorFor = (slide: DeckSlide) => (isDarkSlide(slide) ? '#f8fafc' : theme.text);

  const patchSlide = (id: string, patch: Partial<DeckSlide>) => {
    if (readOnly) return;
    commit({ ...deck, slides: slides.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  };

  const setElements = (slideId: string, elements: DeckElement[]) => {
    if (readOnly) return;
    commit({ ...deck, slides: slides.map((s) => (s.id === slideId ? { ...s, elements } : s)) });
  };

  const updateElement = (id: string, patch: Partial<DeckElement>) => {
    if (!current) return;
    setElements(current.id, current.elements.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const addElement = (patch: Partial<DeckElement> & { type: DeckElement['type'] }) => {
    if (!current) return;
    const count = current.elements.length;
    const offset = (count % 6) * 3;
    const el: DeckElement = {
      id: uid(patch.type),
      x: 12 + offset,
      y: 16 + offset,
      w: 46,
      h: 24,
      rotation: 0,
      opacity: 1,
      fontSize: 3.2,
      color: textColorFor(current),
      align: 'left',
      valign: 'top',
      ...patch,
    };
    setElements(current.id, [...current.elements, el]);
    setSelectedId(el.id);
    setShowInspector(true);
  };

  const removeElement = (id: string) => {
    if (!current) return;
    setElements(current.id, current.elements.filter((e) => e.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const duplicateElement = () => {
    if (!current || !selected) return;
    const copy: DeckElement = { ...selected, id: uid(selected.type), x: clamp(selected.x + 3, 0, 96), y: clamp(selected.y + 3, 0, 96) };
    setElements(current.id, [...current.elements, copy]);
    setSelectedId(copy.id);
  };

  const layerAction = (action: 'front' | 'forward' | 'backward' | 'back') => {
    if (!current || !selected) {
      notify('Sélectionnez un élément.');
      return;
    }
    const list = [...current.elements];
    const i = list.findIndex((e) => e.id === selected.id);
    if (i < 0) return;
    const [el] = list.splice(i, 1);
    if (action === 'front') list.push(el);
    else if (action === 'back') list.unshift(el);
    else if (action === 'forward') list.splice(Math.min(list.length, i + 1), 0, el);
    else list.splice(Math.max(0, i - 1), 0, el);
    setElements(current.id, list);
  };

  const alignElement = (dir: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom') => {
    if (!selected) return;
    if (dir === 'left') updateElement(selected.id, { x: 0 });
    else if (dir === 'center') updateElement(selected.id, { x: (100 - selected.w) / 2 });
    else if (dir === 'right') updateElement(selected.id, { x: 100 - selected.w });
    else if (dir === 'top') updateElement(selected.id, { y: 0 });
    else if (dir === 'bottom') updateElement(selected.id, { y: 100 - selected.h });
    else updateElement(selected.id, { y: (100 - selected.h) / 2 });
  };

  const addSlide = (layoutId?: string) => {
    if (readOnly) return;
    const slide = layoutId ? buildLayout(layoutId, theme, theme.background) : buildLayout('titleBody', theme, theme.background);
    commit({ ...deck, slides: [...slides, slide] });
    setCurrentId(slide.id);
    setSelectedId(null);
  };

  const duplicateSlide = () => {
    if (readOnly || !current) return;
    const copy: DeckSlide = {
      ...JSON.parse(JSON.stringify(current)),
      id: uid('slide'),
      title: `${current.title} (copie)`,
      elements: current.elements.map((e) => ({ ...e, id: uid(e.type) })),
    };
    const next = [...slides];
    next.splice(currentIndex + 1, 0, copy);
    commit({ ...deck, slides: next });
    setCurrentId(copy.id);
  };

  const deleteSlide = () => {
    if (readOnly) return;
    if (slides.length <= 1) {
      notify('Une présentation doit conserver au moins une diapositive.');
      return;
    }
    const next = slides.filter((s) => s.id !== current.id);
    commit({ ...deck, slides: next });
    setCurrentId(next[Math.max(0, currentIndex - 1)].id);
    setSelectedId(null);
  };

  const moveSlide = (dir: -1 | 1) => {
    if (readOnly) return;
    const target = currentIndex + dir;
    if (target < 0 || target >= slides.length) return;
    const next = [...slides];
    [next[currentIndex], next[target]] = [next[target], next[currentIndex]];
    commit({ ...deck, slides: next });
  };

  const insertChart = (chart: DeckChart, elementPatch: Partial<DeckElement> = {}) =>
    addElement({ type: 'chart', x: 10, y: 20, w: 80, h: 60, chart, color: textColorFor(current), ...elementPatch });

  const applyAiSlides = (newSlides: DeckSlide[], mode: 'replace' | 'append' | 'edit', targetIndex?: number) => {
    if (readOnly) return;
    if (mode === 'replace') {
      commit({ ...deck, slides: newSlides });
      setCurrentId(newSlides[0]?.id || '');
    } else if (mode === 'edit' && targetIndex !== undefined && slides[targetIndex]) {
      const merged = slides.map((s, i) => (i === targetIndex ? { ...s, elements: newSlides[0].elements, bg: newSlides[0].bg } : s));
      commit({ ...deck, slides: merged });
      setCurrentId(slides[targetIndex].id);
    } else {
      commit({ ...deck, slides: [...slides, ...newSlides] });
      setCurrentId(newSlides[0]?.id || currentId);
    }
    setSelectedId(null);
    notify('Présentation mise à jour par l’IA.');
  };

  const addChartToSlide = (index: number, chart: DeckChart) => {
    const target = slides[index];
    if (!target) {
      notify('Diapositive introuvable.');
      return;
    }
    const el: DeckElement = {
      id: uid('chart'),
      type: 'chart',
      x: 8,
      y: 18,
      w: 84,
      h: 62,
      chart,
      fill: theme.primary,
      color: isDarkSlide(target) ? '#f8fafc' : theme.text,
    };
    commit({ ...deck, slides: slides.map((s, i) => (i === index ? { ...s, elements: [...s.elements, el] } : s)) });
    setCurrentId(target.id);
    setSelectedId(el.id);
    notify(`Graphique ajouté à la diapositive ${index + 1}.`);
  };

  const addTextSlide = (title: string, body: string) => {
    if (readOnly) return;
    const slide: DeckSlide = {
      id: uid('slide'),
      title,
      bg: theme.background,
      transition: 'fade',
      notes: '',
      elements: [
        textElement(`<b>${escapeHtml(title)}</b>`, { x: 7, y: 8, w: 86, h: 14, fontSize: 4.8, color: theme.primary }, theme),
        textElement(escapeHtml(body).replace(/\n/g, '<br>'), { x: 7, y: 26, w: 86, h: 60, fontSize: 2.9 }, theme),
      ],
    };
    commit({ ...deck, slides: [...slides, slide] });
    setCurrentId(slide.id);
  };

  const printDeck = () => {
    const html = slides
      .map((s) => {
        const texts = s.elements.filter((e) => e.type === 'text' || e.type === 'wordart');
        const strip = (h: string) => h.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
        const title = strip(texts[0]?.html || '') || s.title;
        const body = texts.slice(1).map((e) => strip(e.html || '')).filter(Boolean).join('\n');
        return `<section style="page-break-after:always;padding:24px;box-sizing:border-box;"><h2 style="margin:0 0 12px;font-size:24px;">${escapeHtml(title)}</h2><p style="font-size:14px;white-space:pre-wrap;">${escapeHtml(body)}</p></section>`;
      })
      .join('');
    const frame = document.createElement('iframe');
    frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(frame);
    const doc = frame.contentWindow?.document;
    if (!doc) {
      document.body.removeChild(frame);
      return;
    }
    doc.open();
    doc.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>${escapeHtml(docName)}</title><style>body{font-family:Arial,Helvetica,sans-serif;color:#111;}</style></head><body>${html}</body></html>`);
    doc.close();
    window.setTimeout(() => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      window.setTimeout(() => {
        if (frame.parentNode) document.body.removeChild(frame);
      }, 1500);
    }, 350);
  };

  const applyTheme = (id: string) => {
    const preset = THEME_PRESETS.find((t) => t.id === id);
    if (!preset) return;
    commit({ ...deck, theme: preset.theme, slides: slides.map((s) => ({ ...s, bg: s.bg === deck.theme.background ? preset.theme.background : s.bg })) });
    notify(`Thème « ${preset.label} » appliqué.`);
  };

  const handlers: PowerPointMenuHandlers = {
    save: () => (onSave ? onSave() : notify('Utilisez Ctrl+S pour enregistrer.')),
    duplicate: () => (onDuplicate ? onDuplicate() : notify('Action indisponible ici.')),
    rename: () => (onRename ? setRenameOpen(true) : notify('Renommage indisponible ici.')),
    exportAs: (format) => (onExport ? onExport(format) : notify('Export indisponible.')),
    print: printDeck,
    share: () => (onShare ? onShare() : notify('Action indisponible ici.')),
    history: () => (onHistory ? onHistory() : notify('Action indisponible ici.')),
    close: () => (onClose ? onClose() : notify('Action indisponible ici.')),
    del: () => (onDelete ? onDelete() : notify('Action indisponible ici.')),
    canManage,

    addSlide: () => addSlide(),
    duplicateSlide,
    deleteSlide,
    moveUp: () => moveSlide(-1),
    moveDown: () => moveSlide(1),
    insertLayout: (id) => addSlide(id),

    insert: (kind) => setInsertKind(kind),
    insertText: () => addElement({ type: 'text', html: 'Nouveau texte', fontSize: 3.4 }),

    align: (a) => selected && updateElement(selected.id, { align: a }),
    setBg: (color) => patchSlide(current.id, { bg: color }),
    applyTheme,
    layer: layerAction,
    toggleNotes: () => setShowNotes((v) => !v),

    settings: { present, zoom, showNotes, pageFormat: `${deck.w}x${deck.h}` },
    togglePresent: () => (present ? setPresent(false) : (setPresentIndex(currentIndex), setPresent(true))),
    setZoom,
    setPageFormat: (value) => {
      const [w, h] = value.split('x').map(Number);
      commit({ ...deck, w, h });
    },

    openAi: () => setChatOpen(true),
    publicLink: () => {
      if (!documentId || !currentUser) {
        notify('Enregistrez le document pour créer un lien de présentation.');
        return;
      }
      setShowPublicShare(true);
    },

    notify,
    showShortcuts: () => setShowShortcuts(true),
    showAbout: () => setShowAbout(true),
  };

  const groups = buildPowerPointMenus(handlers).filter((g) => !readOnly || ['file', 'view', 'help'].includes(g.key));

  useEffect(() => {
    if (!present) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPresent(false);
      else if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        setPresentIndex((i) => Math.min(slides.length - 1, i + 1));
      } else if (e.key === 'ArrowLeft') setPresentIndex((i) => Math.max(0, i - 1));
      else if (e.key.toLowerCase() === 'n') setShowNotes((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [present, slides.length]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'F5') {
        e.preventDefault();
        if (present) setPresent(false);
        else {
          setPresentIndex(currentIndex);
          setPresent(true);
        }
        return;
      }
      const mod = e.ctrlKey || e.metaKey;
      if (!mod) return;
      const key = e.key.toLowerCase();
      if (key === 'd') {
        e.preventDefault();
        if (!readOnly) duplicateSlide();
      } else if (key === 'm') {
        e.preventDefault();
        if (!readOnly) addSlide();
      } else if (key === 's') {
        e.preventDefault();
        onSave?.();
      } else if (key === 'p') {
        e.preventDefault();
        printDeck();
      } else if (key === '/') {
        e.preventDefault();
        setShowShortcuts(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [present, readOnly, currentIndex, slides, deck]);

  const aiContext = useMemo(() => {
    const lines = slides.map((s, i) => {
      const texts = s.elements
        .filter((e) => e.type === 'text' || e.type === 'wordart')
        .map((e) => (e.html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .join(' | ');
      return `Diapo ${i + 1} : ${s.title}${texts ? ` — ${texts}` : ''}`;
    });
    return `Présentation « ${docName} » · ${slides.length} diapositives · diapositive courante : ${currentIndex + 1}.\n${lines.join('\n').slice(0, 4000)}`;
  }, [slides, docName, currentIndex]);

  const presentationSlide = slides[presentIndex] || slides[0];

  return (
    <div className="relative flex flex-col h-full bg-background-100">
      <div className="flex items-center gap-1 border-b border-background-200/70 px-2 py-1 bg-background-50">
        <WordMenuBar groups={groups} />
        <button
          onClick={() => setChatOpen((v) => !v)}
          className={`ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium cursor-pointer whitespace-nowrap transition-colors ${
            chatOpen ? 'bg-accent-500 text-background-50' : 'bg-accent-100 text-accent-700 hover:bg-accent-200/70'
          }`}
          title="Ouvrir ou fermer le chat IA"
        >
          <i className="ri-sparkling-line"></i>
          Assistant IA
        </button>
        {documentId && currentUser && (
          <button
            onClick={() => setShowPublicShare(true)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-secondary-100 text-secondary-800 hover:bg-secondary-200/70 cursor-pointer whitespace-nowrap"
            title="Créer un lien de consultation"
          >
            <i className="ri-link"></i>
            Lien de présentation
          </button>
        )}
        {readOnly && (
          <span className="mx-2 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100 text-[11px] font-medium text-foreground-500 whitespace-nowrap">
            <i className="ri-eye-line"></i>
            Lecture seule
          </span>
        )}
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Rail des diapositives */}
        <div className="w-40 flex-shrink-0 border-r border-background-200/70 bg-background-50 flex flex-col">
          <div className="px-2.5 py-2 border-b border-background-200/70 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-foreground-600">Diapositives</span>
            {!readOnly && (
              <button onClick={() => addSlide()} className="w-6 h-6 rounded hover:bg-background-100 flex items-center justify-center text-foreground-600 cursor-pointer" title="Ajouter">
                <i className="ri-add-line text-sm"></i>
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-1.5 space-y-1.5">
            {slides.map((s, i) => (
              <div key={s.id} className="relative group">
                <button
                  onClick={() => {
                    setCurrentId(s.id);
                    setSelectedId(null);
                  }}
                  className={`w-full block rounded border overflow-hidden cursor-pointer transition-colors ${s.id === current.id ? 'border-primary-500 ring-1 ring-primary-300' : 'border-background-200/70 hover:border-background-300'}`}
                  title={s.title}
                >
                  <DeckSlideStatic slide={s} theme={theme} />
                </button>
                <span className="absolute bottom-1 left-1 px-1 py-0.5 rounded bg-black/55 text-white text-[9px]">{i + 1}</span>
                {!readOnly && slides.length > 1 && (
                  <button
                    onClick={() => {
                      commit({ ...deck, slides: slides.filter((x) => x.id !== s.id) });
                      if (s.id === current.id) setCurrentId(slides[Math.max(0, i - 1)].id);
                    }}
                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer"
                    title="Supprimer"
                  >
                    <i className="ri-close-line text-[10px]"></i>
                  </button>
                )}
              </div>
            ))}
          </div>
          {!readOnly && (
            <div className="p-1.5 border-t border-background-200/70">
              <select
                value=""
                onChange={(e) => e.target.value && addSlide(e.target.value)}
                className="w-full px-2 py-1.5 rounded-md border border-background-200/70 bg-white text-[11px] text-foreground-700 cursor-pointer"
                title="Nouvelle diapositive avec mise en page"
              >
                <option value="">+ Mise en page…</option>
                {LAYOUTS.map((l) => (
                  <option key={l.id} value={l.id}>{l.label}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Zone de travail */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-background-50 border-b border-background-200/70 overflow-x-auto">
            {!readOnly && (
              <>
                {[
                  { kind: 'text' as const, icon: 'ri-text', label: 'Texte' },
                  { kind: 'wordart' as const, icon: 'ri-font-size-2', label: 'WordArt' },
                  { kind: 'image' as const, icon: 'ri-image-line', label: 'Image' },
                  { kind: 'shape' as const, icon: 'ri-shapes-line', label: 'Forme' },
                  { kind: 'line' as const, icon: 'ri-subtract-line', label: 'Ligne' },
                  { kind: 'table' as const, icon: 'ri-table-line', label: 'Tableau' },
                  { kind: 'chart' as const, icon: 'ri-bar-chart-box-line', label: 'Graphique' },
                  { kind: 'video' as const, icon: 'ri-video-line', label: 'Vidéo' },
                ].map((b) => (
                  <button
                    key={b.kind}
                    onClick={() => {
                      if (b.kind === 'text') addElement({ type: 'text', html: 'Nouveau texte', fontSize: 3.4 });
                      else if (b.kind === 'wordart') addElement({ type: 'wordart', html: 'WordArt', fontSize: 6, bold: true, color: theme.accent, align: 'center' });
                      else if (b.kind === 'shape') addElement({ type: 'shape', shape: 'roundRect' as DeckShape, fill: theme.primary, x: 30, y: 28, w: 40, h: 30, radius: 14 });
                      else if (b.kind === 'line') addElement({ type: 'line', x: 15, y: 45, w: 70, h: 4, stroke: theme.primary, strokeWidth: 3 });
                      else setInsertKind(b.kind as InsertKind);
                    }}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap"
                    title={b.label}
                  >
                    <i className={b.icon}></i>
                    <span className="hidden xl:inline">{b.label}</span>
                  </button>
                ))}
                <span className="w-px h-5 bg-background-200/70 mx-1"></span>
              </>
            )}
            <button
              onClick={() => (present ? setPresent(false) : (setPresentIndex(currentIndex), setPresent(true)))}
              className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-[11px] font-medium cursor-pointer hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-play-line"></i>
              Présenter
            </button>
            <button
              onClick={() => setShowNotes((v) => !v)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium cursor-pointer whitespace-nowrap ${showNotes ? 'bg-accent-100 text-accent-800' : 'text-foreground-600 hover:bg-background-100'}`}
            >
              <i className="ri-sticky-note-line"></i>
              Notes
            </button>
            <div className="ml-auto flex items-center gap-1">
              <select
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className="px-1.5 py-1 rounded-md border border-background-200/70 bg-white text-[11px] text-foreground-600 cursor-pointer"
                title="Zoom"
              >
                {ZOOMS.map((z) => (
                  <option key={z} value={z}>{z} %</option>
                ))}
              </select>
              <button
                onClick={() => setShowInspector((v) => !v)}
                className={`w-7 h-7 rounded-md flex items-center justify-center cursor-pointer ${showInspector ? 'bg-background-100 text-foreground-800' : 'text-foreground-500 hover:bg-background-100'}`}
                title="Panneau de propriétés"
              >
                <i className="ri-side-bar-line"></i>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto bg-background-100 p-4 md:p-6">
            <div className="mx-auto" style={{ width: `${Math.min(100, zoom)}%`, maxWidth: deck.w }}>
              <div className="flex items-center justify-between mb-2 text-[11px] text-foreground-400">
                <span>
                  Diapositive {currentIndex + 1} / {slides.length}
                </span>
                <span>{deck.w} × {deck.h}</span>
              </div>
              <SlideCanvas
                slide={current}
                theme={theme}
                selectedId={selectedId}
                readOnly={readOnly}
                onSelect={setSelectedId}
                onUpdate={updateElement}
                onBeginEdit={() => undefined}
                onRemove={removeElement}
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  {!readOnly && (
                    <>
                      <button onClick={() => moveSlide(-1)} disabled={currentIndex === 0} className="w-7 h-7 rounded-full bg-white border border-background-200/70 flex items-center justify-center text-foreground-600 disabled:opacity-40 cursor-pointer" title="Monter la diapositive">
                        <i className="ri-arrow-up-line text-sm"></i>
                      </button>
                      <button onClick={() => moveSlide(1)} disabled={currentIndex === slides.length - 1} className="w-7 h-7 rounded-full bg-white border border-background-200/70 flex items-center justify-center text-foreground-600 disabled:opacity-40 cursor-pointer" title="Descendre la diapositive">
                        <i className="ri-arrow-down-line text-sm"></i>
                      </button>
                      <button onClick={duplicateSlide} className="w-7 h-7 rounded-full bg-white border border-background-200/70 flex items-center justify-center text-foreground-600 cursor-pointer" title="Dupliquer la diapositive">
                        <i className="ri-file-copy-line text-sm"></i>
                      </button>
                    </>
                  )}
                </div>
                <p className="text-[11px] text-foreground-400 hidden sm:block">
                  Double-cliquez sur un texte pour le modifier · glissez pour déplacer
                </p>
              </div>

              {showNotes && (
                <div className="mt-3 rounded-lg border border-background-200/70 bg-background-50 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400 mb-1.5">Notes du présentateur</p>
                  <textarea
                    value={current.notes || ''}
                    onChange={(e) => patchSlide(current.id, { notes: e.target.value })}
                    readOnly={readOnly}
                    rows={3}
                    maxLength={500}
                    placeholder="Ce que vous direz pendant cette diapositive…"
                    className="w-full resize-none bg-transparent text-xs text-foreground-700 focus:outline-none"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {showInspector && !readOnly && (
          <div className="hidden lg:block">
            <SlideInspector
              deck={deck}
              slide={current}
              selected={selected}
              readOnly={readOnly}
              onUpdateElement={updateElement}
              onUpdateSlide={(patch) => patchSlide(current.id, patch)}
              onUpdateDeck={(patch) => commit({ ...deck, ...patch })}
              onLayer={layerAction}
              onDuplicate={duplicateElement}
              onDelete={() => selected && removeElement(selected.id)}
              onAlign={alignElement}
            />
          </div>
        )}

        <PresentationAiPanel
          open={chatOpen}
          theme={theme}
          slideCount={slides.length}
          currentSlide={currentIndex}
          historyKey={documentId ? `deck-${documentId}` : undefined}
          getContext={() => aiContext}
          onClose={() => setChatOpen(false)}
          onApplySlides={applyAiSlides}
          onAddChart={addChartToSlide}
          onAddTextSlide={addTextSlide}
        />
      </div>

      <SlideInsertDialogs
        kind={insertKind}
        onClose={() => setInsertKind(null)}
        onImage={(src, link) => {
          addElement({ type: 'image', src, link, x: 22, y: 18, w: 56, h: 62 });
          setInsertKind(null);
        }}
        onVideo={(url) => {
          addElement({ type: 'video', videoUrl: url, x: 12, y: 18, w: 76, h: 62 });
          setInsertKind(null);
        }}
        onTable={(table) => {
          addElement({ type: 'table', table, x: 8, y: 20, w: 84, h: 52 });
          setInsertKind(null);
        }}
        onChart={(chart) => {
          insertChart(chart);
          setInsertKind(null);
        }}
        onLink={(url, label) => {
          addElement({ type: 'text', html: `<a href="${url}" target="_blank" rel="nofollow" style="color:#0f766e;text-decoration:underline">${escapeHtml(label)}</a>`, fontSize: 3, x: 20, y: 40, w: 60, h: 12 });
          setInsertKind(null);
        }}
      />

      {present && presentationSlide && (
        <div className="fixed inset-0 z-[95] bg-foreground-950 flex flex-col">
          <div className="flex items-center justify-between px-5 py-2.5 text-background-50">
            <span className="text-xs font-medium">
              {docName} · {presentIndex + 1} / {slides.length}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setShowNotes((v) => !v)} className={`px-2.5 py-1.5 rounded-full text-[11px] cursor-pointer ${showNotes ? 'bg-background-50/20' : 'hover:bg-background-50/10'}`} title="Notes du présentateur (N)">
                <i className="ri-sticky-note-line mr-1"></i>Notes
              </button>
              <button onClick={() => setPresent(false)} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-foreground-800 cursor-pointer" title="Quitter (Échap)">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 pb-2 min-h-0">
            <div className="w-full max-w-6xl">
              <div key={presentationSlide.id} className="animate-fade-in">
                <DeckSlideStatic slide={presentationSlide} theme={theme} interactive />
              </div>
            </div>
          </div>
          {showNotes && (
            <div className="mx-auto w-full max-w-6xl px-4 pb-3">
              <p className="text-[11px] text-background-50/80 whitespace-pre-wrap max-h-24 overflow-y-auto">
                {presentationSlide.notes || 'Aucune note pour cette diapositive.'}
              </p>
            </div>
          )}
          <div className="flex items-center justify-center gap-3 pb-5">
            <button onClick={() => setPresentIndex((i) => Math.max(0, i - 1))} disabled={presentIndex === 0} className="w-10 h-10 rounded-full border border-background-300/40 text-background-50 flex items-center justify-center disabled:opacity-40 cursor-pointer hover:bg-foreground-800" title="Précédent">
              <i className="ri-arrow-left-line"></i>
            </button>
            <span className="text-[11px] text-foreground-300">{presentIndex + 1} / {slides.length}</span>
            <button onClick={() => setPresentIndex((i) => Math.min(slides.length - 1, i + 1))} disabled={presentIndex === slides.length - 1} className="w-10 h-10 rounded-full border border-background-300/40 text-background-50 flex items-center justify-center disabled:opacity-40 cursor-pointer hover:bg-foreground-800" title="Suivant">
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      )}

      {showPublicShare && documentId && currentUser && (
        <DocsShareModal
          userId={currentUser.id}
          userName={currentUser.name}
          title={docName}
          mode="doc"
          initialDocIds={[documentId]}
          onClose={() => setShowPublicShare(false)}
        />
      )}

      <EditorShortcutsDialog
        open={showShortcuts}
        onClose={() => setShowShortcuts(false)}
        shortcuts={[
          ['Ctrl + S', 'Enregistrer'],
          ['Ctrl + D', 'Dupliquer la diapositive'],
          ['Ctrl + M', 'Nouvelle diapositive'],
          ['Ctrl + P', 'Imprimer'],
          ['F5', 'Lancer la présentation'],
          ['N', 'Afficher les notes en présentation'],
          ['Échap', 'Quitter la présentation'],
          ['Ctrl + /', 'Afficher les raccourcis'],
        ]}
      />
      <EditorAboutDialog
        open={showAbout}
        title="À propos de l'éditeur de présentations"
        description="Un éditeur de diaporamas par positionnement absolu : chaque élément (texte, image, forme, graphique, vidéo) possède sa position, sa taille, sa rotation et son rang dans l'axe Z."
        features={[
          'Diapositives avec mises en page prédéfinies et thèmes',
          'Positionnement absolu, rotation, opacité et calques (axe Z)',
          'Zones de texte, images, formes, lignes, tableaux, graphiques et vidéos',
          'Transitions, animations et notes du présentateur',
          'Mode présentation plein écran',
          'Assistant IA pour créer ou modifier les diapositives',
          'Lien public de consultation (sans compte, sans modification)',
        ]}
        onClose={() => setShowAbout(false)}
      />

      <WordPromptDialog
        open={renameOpen}
        title="Renommer le document"
        label="Nom du document"
        defaultValue={docName}
        confirmLabel="Renommer"
        onConfirm={(value) => {
          onRename?.(value);
          notify('Document renommé.');
        }}
        onClose={() => setRenameOpen(false)}
      />

      {toast && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-[96] animate-toast-in px-4 py-2.5 rounded-lg bg-foreground-950 text-background-50 text-xs font-medium max-w-[90%] text-center">
          {toast}
        </div>
      )}
    </div>
  );
}